"use client";

import { useEffect, useState } from "react";
import { differenceInSeconds, differenceInMinutes, differenceInHours } from "date-fns";
import { Clock, AlertTriangle } from "lucide-react";
import { cn } from "@/lib/utils";

interface CountdownProps {
  targetDate: Date | string;
  label?: string;
  className?: string;
  showIcon?: boolean;
  onExpired?: () => void;
}

export function Countdown({
  targetDate,
  label,
  className,
  showIcon = true,
  onExpired,
}: CountdownProps) {
  const [timeLeft, setTimeLeft] = useState<string>("");
  const [isExpired, setIsExpired] = useState(false);
  const [isUrgent, setIsUrgent] = useState(false);

  useEffect(() => {
    const target = new Date(targetDate);

    const updateCountdown = () => {
      const now = new Date();
      const diffSeconds = differenceInSeconds(target, now);

      if (diffSeconds <= 0) {
        setIsExpired(true);
        setTimeLeft("Expired");
        onExpired?.();
        return;
      }

      const hours = differenceInHours(target, now);
      const minutes = differenceInMinutes(target, now) % 60;
      const seconds = diffSeconds % 60;

      setIsUrgent(hours < 2);

      if (hours > 0) {
        setTimeLeft(`${hours}h ${minutes}m`);
      } else if (minutes > 0) {
        setTimeLeft(`${minutes}m ${seconds}s`);
      } else {
        setTimeLeft(`${seconds}s`);
      }
    };

    updateCountdown();
    const interval = setInterval(updateCountdown, 1000);

    return () => clearInterval(interval);
  }, [targetDate, onExpired]);

  return (
    <div
      className={cn(
        "inline-flex items-center gap-1.5 text-sm",
        isExpired && "text-destructive",
        isUrgent && !isExpired && "text-amber-600",
        !isUrgent && !isExpired && "text-muted-foreground",
        className
      )}
    >
      {showIcon && (
        isExpired || isUrgent ? (
          <AlertTriangle className="h-4 w-4" />
        ) : (
          <Clock className="h-4 w-4" />
        )
      )}
      {label && <span>{label}</span>}
      <span className="font-medium">{timeLeft}</span>
    </div>
  );
}
